<script>
import Content from '@/services/Content'
import { articleJsonToHtml } from '@/helpers/newsArticleContent'
import validationMessages from '@/components/validations'
import NotificationAudienceTargeting from '@/components/notification/NotificationAudienceTargeting'
import { required } from "vuelidate/lib/validators";
import CKEditor from '@ckeditor/ckeditor5-vue2';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";

export default {
  components: {
    validationMessages,
    ckeditor: CKEditor.component,
    flatPickr,
    NotificationAudienceTargeting,
  },
  props: {
    newsData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      csrf_token: localStorage.getItem('csrf_token'),
      tenant: process.env.VUE_APP_TENANT,
      showModal: false,
      tryingToSubmit: false,
      submitted: false,
      source_id: "",
      title: "",
      content: "",
      image: "",
      vertical_image:"",
      published: "",
      language: "",
      newsLanguages: [],
      date: '',
      time: '',
      file: null,
      vertical_file:null,
      content_source:"AZERION",
      acceptedFileTypeErrorMessage:'',
      acceptedFileTypeError:false,
      acceptedFileSizeErrorMessage:'',
      acceptedFileSizeError:false,
      image_removed: false,
      vertical_image_removed: false,
      editor: ClassicEditor,
      editorData: '',
      dateconfig: {
        enableTime: false,
        dateFormat: "Y-m-d",
      },
      timeconfig: {
        enableTime: true,
        noCalendar: true,
        time_24hr: true,
        minuteIncrement: 1,
        dateFormat: "H:i",
      },
      verified_checked: true,
      editorConfig: {
        toolbar: [
          'heading', '|',
          'alignment',          // ← add this
          'bold', 'italic',     // basic styles
          'link',               // link button
          '|',
          'bulletedList', 'numberedList',
          'blockQuote',
          '|',
          'undo', 'redo',
        ]
      },
      accepted_file_types: ['image/jpeg', 'image/png','image/gif','image/webp','image/bmp'],
      accepted_file_size:4096,
      isDragOver: false,
      isPopulating: false,
      editorMountKey: 0,
      // S3 overwrites the same object key; bust browser/CDN cache on each modal open
      imageCacheBust: Date.now(),

    };
  },
  validations() {
    const rules = {
      title: { required },
      content: { required },
      published: { required },
      image: { required },
    };

    if (this.newsLanguages.length > 0) {
      rules.language = { required };
    }

    return rules;
  },
  computed: {
    languageOptions() {
      const options = [...(this.newsLanguages || [])];
      if (this.language && !options.includes(this.language)) {
        options.unshift(this.language);
      }
      return options;
    },
    imagePreviewSrc() {
      return this.withCacheBust(this.image);
    },
    verticalImagePreviewSrc() {
      return this.withCacheBust(this.vertical_image);
    },
  },
  watch: {
    date(newDate) {
      if (!this.isPopulating) {
        this.updatePublished();
      }
    },
    time(newTime) {
      if (!this.isPopulating) {
        this.updatePublished();
      }
    },
    newsData: {
      handler(newData) {
        if (this.showModal && newData && Object.keys(newData).length > 0) {
          this.populateData(newData);
        }
      },
      deep: true,
    }
  },
  methods: {
    refreshNewsData() {
      this.$emit('refreshScreen')
    },

    async loadNewsLanguages() {
      try {
        const response = await Content.getNewsLanguage();
        this.newsLanguages = Array.isArray(response.data) ? response.data : [];
      } catch (error) {
        this.newsLanguages = [];
      }
    },

    async onModalShown() {
      await this.loadNewsLanguages();
      if (this.newsData && Object.keys(this.newsData).length > 0) {
        this.editorMountKey += 1;
        this.populateData(this.newsData);
      }
      this.$nextTick(async () => {
        if (this.$refs.audienceTargeting) {
          await this.$refs.audienceTargeting.onShown();
          await this.$refs.audienceTargeting.loadFromInitialAudience({
            audience: this.newsData.audience || 'ALL',
            audience_group_id: this.newsData.audience_group_id || null,
          });
        }
      });
    },

    withCacheBust(url) {
      if (!url || typeof url !== 'string') {
        return url;
      }
      // Local file previews must not get query params (breaks blob: URLs)
      if (url.startsWith('blob:') || url.startsWith('data:')) {
        return url;
      }
      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}v=${this.imageCacheBust}`;
    },

    populateData(data) {
      this.isPopulating = true;
      this.imageCacheBust = Date.now();

      this.source_id = data.source_id || "";
      this.title = data.title || "";
      this.language = data.lang || "";
      if (data.content_original) {
        this.content = data.content_original;
      } else if (data.content) {
        this.content = articleJsonToHtml(data.content);
      } else {
        this.content = "";
      }
      this.content_source = data.content_source || "AZERION";
      this.file = null;
      this.vertical_file = null;
      this.image_removed = false;
      this.vertical_image_removed = false;
      this.image = data.image || "";
      this.vertical_image = data.vertical_image || "";

      // Parse published date/time
      if (data.published) {
        let publishedDate;

        // Handle different date formats
        if (data.published.includes('.') && data.published.includes(' ')) {
          // Format: DD.MM.YYYY HH:MM (e.g., "27.05.2026 12:00")
          const [datePart, timePart] = data.published.split(' ');
          const [day, month, year] = datePart.split('.');

          this.date = `${year}-${month}-${day}`;
          this.time = timePart;
          this.published = `${this.date} ${this.time}`;
        } else if (data.published.includes('T')) {
          // ISO format: 2024-01-15T10:30:00
          publishedDate = new Date(data.published);
          if (!isNaN(publishedDate.getTime())) {
            const year = publishedDate.getFullYear();
            const month = String(publishedDate.getMonth() + 1).padStart(2, '0');
            const day = String(publishedDate.getDate()).padStart(2, '0');
            this.date = `${year}-${month}-${day}`;

            const hours = String(publishedDate.getHours()).padStart(2, '0');
            const minutes = String(publishedDate.getMinutes()).padStart(2, '0');
            this.time = `${hours}:${minutes}`;

            this.published = `${this.date} ${this.time}`;
          }
        } else if (data.published.includes('-') && data.published.includes(' ')) {
          // SQL format: 2024-01-15 10:30:00
          publishedDate = new Date(data.published.replace(' ', 'T'));
          if (!isNaN(publishedDate.getTime())) {
            const year = publishedDate.getFullYear();
            const month = String(publishedDate.getMonth() + 1).padStart(2, '0');
            const day = String(publishedDate.getDate()).padStart(2, '0');
            this.date = `${year}-${month}-${day}`;

            const hours = String(publishedDate.getHours()).padStart(2, '0');
            const minutes = String(publishedDate.getMinutes()).padStart(2, '0');
            this.time = `${hours}:${minutes}`;

            this.published = `${this.date} ${this.time}`;
          }
        } else {
          // Fallback
          publishedDate = new Date(data.published);
          if (!isNaN(publishedDate.getTime())) {
            const year = publishedDate.getFullYear();
            const month = String(publishedDate.getMonth() + 1).padStart(2, '0');
            const day = String(publishedDate.getDate()).padStart(2, '0');
            this.date = `${year}-${month}-${day}`;

            const hours = String(publishedDate.getHours()).padStart(2, '0');
            const minutes = String(publishedDate.getMinutes()).padStart(2, '0');
            this.time = `${hours}:${minutes}`;

            this.published = `${this.date} ${this.time}`;
          }
        }
      } else {
        this.date = '';
        this.time = '';
        this.published = '';
      }

      this.$nextTick(() => {
        this.isPopulating = false;
      });
    },

    editNewsArticle() {
      if(this.acceptedFileTypeError || this.acceptedFileSizeError) return;

      this.tryingToSubmit = true;
      this.submitted = true;
      this.$v.$touch();

      if (this.$v.$invalid) {
        this.tryingToSubmit = false;
        return;
      }

      const audienceError = this.$refs.audienceTargeting.validate();
      if (audienceError) {
        this.failedmsg(audienceError);
        this.tryingToSubmit = false;
        return;
      }

      const formData = new FormData();
      formData.append("source", this.tenant);
      formData.append("title", this.title);
      formData.append("content_original", this.content);
      formData.append("published", this.published);
      if (this.newsLanguages.length > 0 || this.language) {
        formData.append("lang", this.language);
      }
      formData.append("csrf_token", this.csrf_token);
      formData.append('_method', 'PATCH');


      // Only append new files if they exist
      if (this.file) {
        formData.append("image", this.file);
      }
      if (this.vertical_file) {
        formData.append("vertical_image", this.vertical_file);
      }

      // Track if images were removed
      if (this.image_removed) {
        formData.append("image_removed", "1");
      }
      if (this.vertical_image_removed) {
        formData.append("vertical_image_removed", "1");
      }
      this.$refs.audienceTargeting.appendToFormData(formData);

      Content.editNewsArticle(this.source_id, formData)
          .then(() => {
            this.successmsg("News Article Updated!");
            this.closeModal();
            this.refreshNewsData();
          })
          .catch((error) => {
            const errorMsg = error.response?.data?.error || "Failed to update news article. Please try again.";
            this.failedmsg(errorMsg);
          })
          .finally(() => {
            this.tryingToSubmit = false;
          });
    },
    closeModal() {
      this.showModal = false;
    },
    openDatePicker() {
      this.$refs.datePicker.$el.click();
    },
    openTimePicker() {
      this.$refs.timePicker.$el.click();
    },
    updatePublished() {
      if (this.date && this.time) {
        this.published = `${this.date} ${this.time}`;
      } else if (this.date) {
        this.published = this.date;
      } else {
        this.published = '';
      }
    },
    triggerFileInput() {
      this.$refs['file-input'].click();
    },
    triggerVerticalFileInput() {
      this.$refs['vertical-file-input'].click();
    },
    handleFileDrop(event) {
      this.isDragOver = false;
      const files = event.dataTransfer && event.dataTransfer.files;
      if (files && files.length > 0) {
        const file = files[0];
        const syntheticEvent = {
          target: {
            files: [file]
          }
        };
        this.selectFile(syntheticEvent);
      }
    },
    handleVerticalFileDrop(event) {
      this.isDragOver = false;
      const files = event.dataTransfer && event.dataTransfer.files;
      if (files && files.length > 0) {
        const file = files[0];
        const syntheticEvent = {
          target: {
            files: [file]
          }
        };
        this.selectVerticalFile(syntheticEvent);
      }
    },
    selectFile(event) {
      if (this.image && this.image.startsWith('blob:')) {
        URL.revokeObjectURL(this.image);
      }
      const file = event.target.files[0];
      let bytes = file.size;
      let sizeInKB = bytes / 1024;
      const formatedKB = sizeInKB.toFixed(2);

      if (formatedKB > this.accepted_file_size) {
        this.acceptedFileSizeErrorMessage = 'File size too large | Maximum allowed size is ' + this.accepted_file_size + ' KB';
        this.acceptedFileSizeError = true;
      }else{
        this.acceptedFileSizeErrorMessage = '';
        this.acceptedFileSizeError = false;
      }
      if(!this.accepted_file_types.includes(file.type)) {
        this.acceptedFileTypeErrorMessage = 'File not accepted | Accepted formats are png, jpg, gif, bmp, webp';
        this.acceptedFileTypeError = true;
      }else{
        this.acceptedFileTypeErrorMessage = '';
        this.acceptedFileTypeError = false;
      }
      this.file = file;
      this.image = URL.createObjectURL(file);
      this.image_removed = false;
    },
    selectVerticalFile(event) {
      if (this.vertical_image && this.vertical_image.startsWith('blob:')) {
        URL.revokeObjectURL(this.vertical_image);
      }
      const file = event.target.files[0];
      let bytes = file.size;
      let sizeInKB = bytes / 1024;
      const formatedKB = sizeInKB.toFixed(2);

      if (formatedKB > this.accepted_file_size) {
        this.acceptedFileSizeErrorMessage = 'File size too large | Maximum allowed size is ' + this.accepted_file_size + ' KB';
        this.acceptedFileSizeError = true;
      }else{
        this.acceptedFileSizeErrorMessage = '';
        this.acceptedFileSizeError = false;
      }
      if(!this.accepted_file_types.includes(file.type)) {
        this.acceptedFileTypeErrorMessage = 'File not accepted | Accepted formats are png, jpg, gif, bmp, webp';
        this.acceptedFileTypeError = true;
      }else{
        this.acceptedFileTypeErrorMessage = '';
        this.acceptedFileTypeError = false;
      }
      this.vertical_file = file;
      this.vertical_image = URL.createObjectURL(file);
      this.vertical_image_removed = false;
    },
    removeImage() {
      this.file = null;
      if (this.image && this.image.startsWith('blob:')) {
        URL.revokeObjectURL(this.image);
      }
      this.image = "";
      if (this.$refs['file-input']) {
        this.$refs['file-input'].value = '';
      }
      this.image_removed = true;
    },
    removeVerticalImage() {
      this.vertical_file = null;
      if (this.vertical_image && this.vertical_image.startsWith('blob:')) {
        URL.revokeObjectURL(this.vertical_image);
      }
      this.vertical_image = "";
      if (this.$refs['vertical-file-input']) {
        this.$refs['vertical-file-input'].value = '';
      }
      this.vertical_image_removed = true;
    },

    resetProps() {
      if (this.$refs.audienceTargeting) {
        this.$refs.audienceTargeting.reset();
      }
      this.submitted = false;
      this.source_id = '';
      this.title = '';
      this.content = '';
      if (this.image && this.image.startsWith('blob:')) URL.revokeObjectURL(this.image);
      if (this.vertical_image && this.vertical_image.startsWith('blob:')) URL.revokeObjectURL(this.vertical_image);
      this.image = '';
      this.vertical_image = '';
      this.file = null;
      this.vertical_file = null;
      this.published = '';
      this.language = '';
      this.date = '';
      this.time = '';
      this.content_source = 'AZERION';
      this.verified_checked = true;
      this.isDragOver = false;
      this.image_removed = false;
      this.vertical_image_removed = false;
    },
  }
};
</script>

<template>
  <b-modal id="edit_news" size="lg" v-model="showModal" @shown="onModalShown" @hidden="resetProps" title-class="font-18" no-enforce-focus>
    <template v-slot:modal-header>
      <div class="w-100">
        <h5 class="modal-title mb-0">Edit News Article</h5>
        <p class="text-muted mb-0 mt-2">Update the news article information</p>
      </div>
      <button type="button" class="btn-close" @click="closeModal" aria-label="Close"></button>
    </template>
    <form @submit.prevent="editNewsArticle">
        <div class="card mb-3">
          <div class="card-body py-2">
            <h4 class="card-title mb-1">Basic Information</h4>
            <p class="text-muted mb-3">Essential details about your news article</p>
            <div class="mb-2">
              <label class="mb-1">Title *</label>
              <b-form-input v-model="title" placeholder="e.g., Industry Insights: The Future of Technology"></b-form-input>
            </div>
            <validationMessages
                  v-if="submitted"
                  :fieldName="'Title'"
                  :validationName="$v.title">
            </validationMessages>
            <div class="row mb-2">
              <div class="col-4">
                <label class="mb-1">
                  <i class="uil uil-calendar-alt me-1"></i>Date *
                </label>
                <div class="position-relative">
                  <flat-pickr
                    ref="datePicker"
                    v-model="date"
                    :config="dateconfig"
                    class="form-control pe-4"
                    name="date"
                  />
                  <i class="uil uil-calendar-alt position-absolute top-50 end-0 translate-middle-y me-2 text-muted"
                     style="cursor: pointer; pointer-events: all;"
                     @click="openDatePicker"></i>
                </div>
              </div>
              <div class="col-4">
                <label class="mb-1">
                  <i class="uil uil-clock me-1"></i>Time *
                </label>
                <div class="position-relative">
                  <flat-pickr
                    ref="timePicker"
                    v-model="time"
                    :config="timeconfig"
                    class="form-control pe-4"
                    name="time"
                  />
                  <i class="uil uil-clock position-absolute top-50 end-0 translate-middle-y me-2 text-muted"
                     style="cursor: pointer; pointer-events: all;"
                     @click="openTimePicker"></i>
                </div>
              </div>
              <div v-if="newsLanguages.length > 0" class="col-4">
                <label class="mb-1">Language *</label>
                <select v-model="language" class="form-select">
                  <option value="" disabled>Select language</option>
                  <option v-for="lang in languageOptions" :key="lang" :value="lang">
                    {{ String(lang).toUpperCase() }}
                  </option>
                </select>
                <validationMessages
                  v-if="submitted"
                  :fieldName="'Language'"
                  :validationName="$v.language">
                </validationMessages>
              </div>
            </div>
            <validationMessages
              v-if="submitted"
              :fieldName="'Published'"
              :validationName="$v.published"
            ></validationMessages>
          </div>
        </div>
        <div class="card mb-3">
          <div class="card-body py-2">
            <h4 class="card-title mb-1">
              <i class="uil uil-image me-2"></i>Header Image *
            </h4>
            <p class="text-muted mb-3">Upload a banner image for the news article (recommended: 1200x400px)</p>
            <validationMessages
              v-if="submitted"
              :fieldName="'Header Image'"
              :validationName="$v.image"
            ></validationMessages>
            <b-form-group label-for="image" class="mb-3">
              <!-- Image Preview -->
              <div v-if="image" class="mb-3">
                <div class="image-preview-container position-relative" style="width: 100%; min-height: 200px; background: #f8f9fa; border-radius: 8px; overflow: hidden;">
                  <img :src="imagePreviewSrc" alt="Preview" style="width: 100%; height: 100%; object-fit: cover;">

                  <!-- Text Overlay (Bottom Left) -->
                  <div class="position-absolute" style="bottom: 15px; left: 15px; background: rgba(0,0,0,0.6); color: white; padding: 8px 12px; border-radius: 4px;">
                    <div style="font-size: 14px; font-weight: 600; margin-bottom: 2px;">Header Image Preview</div>
                    <div style="font-size: 12px; opacity: 0.9;">This is how your image will appear</div>
                  </div>

                  <!-- Remove Button (Bottom Right) -->
                  <button
                    type="button"
                    @click="removeImage"
                    class="btn btn-sm position-absolute"
                    style="bottom: 15px; right: 15px; background: rgba(255,255,255,0.95); border: 1px solid #dee2e6; color: #495057; font-size: 12px; padding: 4px 12px; display: flex; align-items: center;"
                  >
                    <i class="uil uil-times me-1"></i>Remove
                  </button>
                </div>
                <p v-if="acceptedFileTypeError" class="text-danger mt-2 mb-0">{{acceptedFileTypeErrorMessage}}</p>
                <p v-if="acceptedFileSizeError" class="text-danger mt-2 mb-0">{{acceptedFileSizeErrorMessage}}</p>
              </div>

              <!-- Upload Area -->
              <div
                v-if="!image"
                class="upload-area text-center position-relative"
                :class="{'border-primary': isDragOver}"
                @dragover.prevent="isDragOver = true"
                @dragleave.prevent="isDragOver = false"
                @drop.prevent="handleFileDrop"
                style="
                  cursor: pointer;
                  min-height: 220px;
                  display: flex; flex-direction: column; justify-content: center; align-items: center;
                  border: 2px dashed #cbd5e1;
                  border-radius: 12px;
                  padding: 28px;
                  background: #fff;
                "
                @click="triggerFileInput"
              >
                <div style="
                  width: 72px; height: 72px; border-radius: 9999px;
                  background: #eef2ff; display: flex; align-items: center; justify-content: center;
                  margin-bottom: 12px;
                ">
                  <i class="uil uil-upload" style="font-size: 28px; color: #6b7280;"></i>
                </div>
                <div class="mb-1" style="font-weight: 500; color: #111827;">
                  Drag and drop an image, or click to browse
                </div>
                <div class="text-muted mb-3" style="font-size: 13px;">
                  PNG, JPG, GIF, WEBP, BMP up to 4MB
                </div>
                <button type="button" class="btn btn-light btn-sm" @click.stop="triggerFileInput">
                  <i class="uil uil-upload me-1"></i>
                  Choose File
                </button>
                <input
                  ref="file-input"
                  type="file"
                  @change="selectFile"
                  accept="image/*"
                  style="display: none;"
                />
                <p v-if="acceptedFileTypeError" class="text-danger mt-2 mb-0">{{acceptedFileTypeErrorMessage}}</p>
                <p v-if="acceptedFileSizeError" class="text-danger mt-2 mb-0">{{acceptedFileSizeErrorMessage}}</p>
              </div>
            </b-form-group>
          </div>
        </div>

        <div class="card mb-3">
          <div class="card-body py-2">
            <h4 class="card-title mb-1">
              <i class="uil uil-image me-2"></i>Vertical Image
            </h4>
            <p class="text-muted mb-3">Upload a vertical image for the news article (recommended: 400x1200px)</p>
            <b-form-group label-for="vertical_image" class="mb-3">
              <!-- Image Preview -->
              <div v-if="vertical_image" class="mb-3">
                <div class="image-preview-container position-relative" style="width: 100%; min-height: 200px; background: #f8f9fa; border-radius: 8px; overflow: hidden;">
                  <img :src="verticalImagePreviewSrc" alt="Preview" style="width: 100%; height: 100%; object-fit: cover;">

                  <!-- Text Overlay (Bottom Left) -->
                  <div class="position-absolute" style="bottom: 15px; left: 15px; background: rgba(0,0,0,0.6); color: white; padding: 8px 12px; border-radius: 4px;">
                    <div style="font-size: 14px; font-weight: 600; margin-bottom: 2px;">Vertical Image Preview</div>
                    <div style="font-size: 12px; opacity: 0.9;">This is how your vertical image will appear</div>
                  </div>

                  <!-- Remove Button (Bottom Right) -->
                  <button
                    type="button"
                    @click="removeVerticalImage"
                    class="btn btn-sm position-absolute"
                    style="bottom: 15px; right: 15px; background: rgba(255,255,255,0.95); border: 1px solid #dee2e6; color: #495057; font-size: 12px; padding: 4px 12px; display: flex; align-items: center;"
                  >
                    <i class="uil uil-times me-1"></i>Remove
                  </button>
                </div>
                <p v-if="acceptedFileTypeError" class="text-danger mt-2 mb-0">{{acceptedFileTypeErrorMessage}}</p>
                <p v-if="acceptedFileSizeError" class="text-danger mt-2 mb-0">{{acceptedFileSizeErrorMessage}}</p>
              </div>

              <!-- Upload Area -->
              <div
                v-if="!vertical_image"
                class="upload-area text-center position-relative"
                :class="{'border-primary': isDragOver}"
                @dragover.prevent="isDragOver = true"
                @dragleave.prevent="isDragOver = false"
                @drop.prevent="handleVerticalFileDrop"
                style="
                  cursor: pointer;
                  min-height: 220px;
                  display: flex; flex-direction: column; justify-content: center; align-items: center;
                  border: 2px dashed #cbd5e1;
                  border-radius: 12px;
                  padding: 28px;
                  background: #fff;
                "
                @click="triggerVerticalFileInput"
              >
                <div style="
                  width: 72px; height: 72px; border-radius: 9999px;
                  background: #eef2ff; display: flex; align-items: center; justify-content: center;
                  margin-bottom: 12px;
                ">
                  <i class="uil uil-upload" style="font-size: 28px; color: #6b7280;"></i>
                </div>
                <div class="mb-1" style="font-weight: 500; color: #111827;">
                  Drag and drop a vertical image, or click to browse
                </div>
                <div class="text-muted mb-3" style="font-size: 13px;">
                  PNG, JPG, GIF, WEBP, BMP up to 4MB
                </div>
                <button type="button" class="btn btn-light btn-sm" @click.stop="triggerVerticalFileInput">
                  <i class="uil uil-upload me-1"></i>
                  Choose File
                </button>
                <input
                  ref="vertical-file-input"
                  type="file"
                  @change="selectVerticalFile"
                  accept="image/*"
                  style="display: none;"
                />
                <p v-if="acceptedFileTypeError" class="text-danger mt-2 mb-0">{{acceptedFileTypeErrorMessage}}</p>
                <p v-if="acceptedFileSizeError" class="text-danger mt-2 mb-0">{{acceptedFileSizeErrorMessage}}</p>
              </div>
            </b-form-group>
          </div>
        </div>

        <NotificationAudienceTargeting ref="audienceTargeting" entity-label="news article" />

        <div class="card mb-3">
          <div class="card-body py-2">
            <h4 class="card-title mb-1">
              <i class="uil uil-file-alt me-2"></i>Content
            </h4>
            <p class="text-muted mb-3">Write the full news article content using the rich text editor</p>
            <b-form-group label-for="content" class="mb-3">
              <ckeditor :key="editorMountKey" :editor="editor" v-model="content" :config="editorConfig"></ckeditor>
              <validationMessages
                v-if="submitted"
                :fieldName="'Content'"
                :validationName="$v.content">
              </validationMessages>
            </b-form-group>
          </div>
        </div>

        <input type="hidden" name="csrf_token" v-model="csrf_token">
      </form>
        <template v-slot:modal-footer>
            <b-button variant="light" @click="closeModal">Close</b-button>
            <b-button variant="primary" @click="editNewsArticle" :disabled="tryingToSubmit">
                <b-spinner v-show="tryingToSubmit" small></b-spinner>
                Update</b-button>
        </template>

    </b-modal>
</template>

<style>
/* Ensure CKEditor popups appear above Bootstrap modal */
.ck.ck-balloon-panel {
  z-index: 10000 !important;
}
</style>
